import React, { useContext } from 'react';
import {
  forwardRef,
  useStyleConfig,
  Box,
  IconButton as ChakraIconButton,
  IconButtonProps as ChakraIconButtonProps,
  Flex,
  As,
} from '@chakra-ui/react';
import {
  ColorMode,
  useColorMode,
} from '@allergan-data-labs/alle-elements-core';
import { ProgressCircle } from '@allergan-data-labs/alle-elements-progress-circle';
import {
  ButtonVariants,
  ButtonColorScheme,
  ButtonSizes,
  getVariant,
  getColorScheme,
  getSize,
  mapButtonSizeToProgressCircleSize,
  mapButtonColorSchemeToProgressColorScheme,
} from './button';
import { getInternalIconStyles } from './icon-button.theme';
import { ButtonGroupContext } from './button-context';
import { getVariantStyles } from './button.theme';

export type IconButtonShape = 'square' | 'circle';

export interface IconButtonProps extends ChakraIconButtonProps {
  icon?: React.ReactElement;
  'aria-label': string;
  /** The component or element to render. If none provided will render button. */
  as?: As;
  /** The visual style of the IconButton */
  variant?: ButtonVariants;
  /** The shape of the IconButton */
  shape?: IconButtonShape;
  /** The size of the IconButton */
  size?: ButtonSizes;
  /** The visual color appearance of the IconButton */
  colorScheme?: ButtonColorScheme;
  isDisabled?: boolean;
  /**
   * If `true`, the button will show a spinner and will be disabled.
   * @default false
   */
  isLoading?: boolean;
  /** The color mode that the button will display.
   * @default light
   */
  colorMode?: ColorMode;
  /** Whether the button is a standalone feature or has a supporting function as part of more complex component */
  isStandalone?: boolean;
}

/**
 * IconButton renders an icon within a button.
 */
export const IconButton = forwardRef<IconButtonProps, typeof ChakraIconButton>(
  (props, ref) => {
    const {
      as,
      variant,
      colorScheme,
      size,
      isLoading,
      isDisabled,
      icon,
      colorMode: propColorMode,
      shape = 'square',
      isStandalone = true,
      ...otherProps
    } = props;
    const { colorMode: contextColorMode } = useColorMode();
    const buttonGroupProps = useContext(ButtonGroupContext);

    const buttonVariant = getVariant(variant, buttonGroupProps.variant);
    const buttonColorScheme = getColorScheme(
      colorScheme,
      buttonGroupProps.colorScheme
    );
    const buttonSize = getSize(size, buttonGroupProps.size);
    const buttonColorMode =
      propColorMode || buttonGroupProps?.colorMode || contextColorMode;

    const baseStyles = useStyleConfig('Button', {
      size: size,
    });
    const variantStyles = getVariantStyles(
      buttonVariant,
      buttonColorScheme,
      buttonColorMode
    );

    if (shape === 'circle') {
      baseStyles.borderRadius = 'Circle';
    } else {
      baseStyles.borderRadius = 'Small';
    }

    const styles = {
      ...baseStyles,
      ...variantStyles,
    };

    const RenderIconOrLoader = () => {
      return (
        <Flex
          position="relative"
          justifyContent="center"
          alignItems="center"
          sx={getInternalIconStyles(buttonSize, isStandalone)}
        >
          {isLoading ? (
            <Box position="absolute">
              <ProgressCircle
                colorScheme={mapButtonColorSchemeToProgressColorScheme(
                  buttonColorScheme
                )}
                colorMode={buttonColorMode}
                size={mapButtonSizeToProgressCircleSize(
                  buttonSize,
                  isStandalone
                )}
              />
            </Box>
          ) : (
            icon
          )}
        </Flex>
      );
    };
    return (
      <ChakraIconButton
        ref={ref}
        as={as}
        __css={styles}
        icon={<RenderIconOrLoader />}
        isDisabled={isLoading || isDisabled}
        {...otherProps}
      />
    );
  }
);
